<template>
  <v-dialog :value="value" persistent :width="isExistingLayout ? 1000 : 500" @click:outside="$emit('closeLayoutBuilder')">
    <v-card color="fill">
      <v-card-title class="accent--text font-weight-light">
        {{isExistingLayout ? 'Layout Builder' : 'Create Layout'}}
        <v-spacer/>
        <v-menu v-if="isExistingLayout" offset-y offset-x left bottom :nudge-right="5" :nudge-top="5">
          <template v-slot:activator="{ on, attrs }">
            <v-btn small v-bind="attrs" v-on="on" icon color="delete" class="white--text">
              <v-icon>mdi-trash-can</v-icon>
            </v-btn>
          </template>
          <v-card class="pa-6">
            <v-row justify="center" class="py-4">
              <span>Delete Layout?</span>
            </v-row>
            <v-row>
              <v-btn text :class="body">Cancel</v-btn>
              <v-btn :class="body" class="primary white--text ml-2" @click="deleteLayout">Confirm</v-btn>
            </v-row>
          </v-card>
        </v-menu>
      </v-card-title>
      <v-container class="ma-0 pa-4" v-resize="onResize">
        <v-form ref="form">
          <v-row align="center" dense style="height:50px;">
            <v-col :cols="isExistingLayout ? 5 : 12">
              <v-text-field :class="body" autofocus background-color="#fff" dense outlined label="Layout Name"
                v-model="layout.ad_name" autocomplete="off" :rules="[v => !!v ||  'Layout Name Required']">
              </v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
      <v-container v-if="isExistingLayout" class="pa-1" fluid>
        <v-row dense>
          <v-col md="3" lg="3" xl="3" class="pt-1 pb-0 px-0">
            <v-toolbar height="32" flat class="accent white--text">
              <v-toolbar-title class="text-caption font-weight-bold">Pages</v-toolbar-title>
            </v-toolbar>
            <v-tabs vertical background-color="fill" color="primary" light class="pt-0"
              hide-slider :style="tabsHeight" v-model="tab">
              <v-tab v-for="(page, index) in pages" :key="index" @change="adPage=page"
                class="text-capitalize font-weight-normal text-body-2">
                <span :class="adPage===page ? 'font-weight-bold' : 'font-weight-normal'"> {{ page.name }}</span>
                <v-spacer/>
                <v-icon v-if="adPage===page">mdi-chevron-right</v-icon>
              </v-tab>
            </v-tabs>
          </v-col>
          <v-col class="mt-1 pt-0 px-0" :style="colBorder">
            <v-data-table disabled :headers="headers" :items-per-page=-1 :items.sync="pagePositions" :search="search"
              :loading="loading" v-model="selected" item-key="id" :class="table" show-select dense fixed-header
              :height="tableHeight" hide-default-footer class="pa-0 ma-0" :item-class="itemRowBackground">
              <template #[`header.data-table-select`]="{ on, props }">
                <v-simple-checkbox :ripple="false" v-on="on" v-bind="props" dark />
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
      <v-divider v-if="isExistingLayout" />
      <v-toolbar flat color="fill">
        <v-spacer />
        <v-btn :class="body" text @click="$emit('closeLayoutBuilder')">
          {{isExistingLayout ? 'Close': 'Cancel'}}
        </v-btn>
        <v-btn
          :class="body"
          class="save white--text ml-2"
          :disabled="!featureDifference && !layoutDifference"
          @click="isExistingLayout ? updateLayout() : createLayout()">
            {{isExistingLayout ? 'Update' : 'Save'}}
        </v-btn>
      </v-toolbar>
    </v-card>
  </v-dialog>
</template>
<script>
import { displayHelpers } from '@/mixins/display-helpers'
export default {
  name: 'LayoutBuilder',
  props: [
    'value',
    'selectedLayout',
    'defaultParty'
  ],
  mixins: [displayHelpers],
  data: () => ({
    adPage: {},
    existingFeatures: [],
    existingLayoutName: '',
    loading: false,
    page: {},
    pagePositions: [],
    pages: [],
    search: '',
    selected: [],
    tab: 0,
    tableHeight: '',
    layout: {}
  }),
  computed: {
    featureDifference () {
      const existingIds = new Set(this.existingFeatures.map(f => f.id))
      const selectedIds = new Set(this.selected.map(f => f.id))
      return existingIds.size !== selectedIds.size || 
        ![...existingIds].every(id => selectedIds.has(id))
    },
    layoutDifference () {
      return this.existingLayoutName !== this.layout.ad_name
    },
    isExistingLayout () {
      return !!this.layout.id
    },
    headers () {
      return [
        { sortable: false, class: 'accent white--text', value: 'data-table-select' },
        { text: 'Feature Position', sortable: true, filterable: true, class: 'accent white--text', value: 'name' }
      ]
    },
    tabsHeight () {
      switch (this.$vuetify.breakpoint.name) {
        case 'xl': return 'height:610px;'
        case 'lg': return 'height:500px;'
        case 'md': return 'height:485px;'
        default: return '5em';
      }
    },
    colBorder () {
      return `
        border:1px solid #d7d7d7;
        border-bottom: none;
        border-top: none;
      `
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (value) {
        if (value) {
          this.tableHeight = window.innerHeight - 280
          this.getAdPages()
        }
      }
    },
    adPage: {
      handler (newValue, oldValue) {
        if (oldValue && newValue)  {
          this.getPagePositions()
        }
      }
    }
  },
  created () {
    if (this.selectedLayout) {
      this.layout = this._.cloneDeep(this.selectedLayout)
    }
    if (this.layout.ad_name) this.existingLayoutName = this.layout.ad_name
  },
  methods: {
    onResize () {
      this.tableHeight = window.innerHeight - 330
    },
    itemRowBackground (item) {
      return this.pagePositions.indexOf(item) % 2 !== 1 ? 'grey lighten-2' : ''
    },
    async getAdPages () {
      try {
        const { data } = await this.$KeyValues.getAdPages()
        this.pages = data
        this.adPage = this.pages.find(page => page.constant === 'PAGE_1')
      } catch (e) {
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Getting ad pages failed due to ${e?.response?.data?.message || e.message}` })
      }
    },
    async getPagePositions () {
      this.loading = true
      try {
        const { data } = await this.$KeyValues.getPagePositions(this.adPage.constant)
        this.pagePositions = data
      } catch (e) {
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Getting page positions failed due to ${e?.response?.data?.message || e.message}` })
      } finally {
        if (this.isExistingLayout) {
          this.getExistingFeatures()
        } else {
          this.loading = false
        }
      }
    },
    async getExistingFeatures () {
      try {
        const { data } = await this.$Features.getAdPageFeatures(this.layout.id, this.adPage.id, { limit: 200 })
        this.existingFeatures = data.map(ef => {
          return {
            id: ef.position.id,
            feature_id: ef.id
          }
        })
        this.existingFeatures.forEach(f => {
          const pos = this.pagePositions.find(p => p.id === f.id)
          if (pos && !this.selected.find(s => s.id === pos.id)) this.selected.push(pos)
        })
      } catch (e) {
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Getting existing features failed due to ${e?.response?.data?.message || e.message}` })
      } finally {
        this.loading = false
      }
    },
    async saveFeatures () {
      try {
        this.loading = true
        let addFeatures = this._.differenceBy(this.selected, this.existingFeatures, 'id')
        addFeatures = [...new Set(addFeatures)]
        let removeFeatures = this._.differenceBy(this.existingFeatures, this.selected, 'id')
        removeFeatures = [...new Set(removeFeatures)]
        let addPromises = []
        let removePromises = []
        if (addFeatures.length > 0) {
          addFeatures.forEach(af => {
            const feature = {
              ad_id: this.layout.id,
              feature_text: '',
              non_promo_items: [],
              notes: '',
              page: this.adPage,
              position: {
                id: af.id
              },
              promo_item_group_ids: [],
              count:0,
              ad_retail:0.00,
              brand_combo:''
            }
            addPromises.push(this.$Features.createAdFeature(feature))
          })
        }
        if (removeFeatures.length > 0) {
          removeFeatures.map(o => {
            removePromises.push(this.$Features.deleteAdFeature(o.feature_id))
          })
        }
        await Promise.allSettled(addPromises)
        await Promise.allSettled(removePromises)
      } catch {
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to Save Features` })
      } finally {
        this.loading = false
      }
    },
    async getLayout (layoutID) {
      try {
        const { data } = await this.$Layouts.getLayoutbyId(layoutID)
        this.layout = data
      } catch(e) {
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to Get Layout` })
      }
    },
    async createLayout () {
      const valid = this.$refs.form.validate()
      if (valid) {
        const layout = {
          features: [],
          ad_party_id: this.defaultParty.id,
          ad_name: this.layout.ad_name
        }
        try {
          const { data } = await this.$Layouts.createLayout(layout)
          this.layout.id = data.id
          this.$emit('refresh')
          this.getLayout(this.layout.id)
          this.$store.dispatch('setSnackbar', { status: 'success', text: `${this.layout.ad_name} Created` })
        } catch {
          this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to Create Layout` })
        }
      }
    },
    async updateLayout () {
      try {
        if (this.featureDifference) {
          await this.saveFeatures()
        }
        if (this.layoutDifference) {
          this.layout.order_grid_submitted = false
          await this.$Layouts.updateLayout(this.layout.id, this.layout)
        }
        if (this.featureDifference || this.layoutDifference) {
          this.$store.dispatch('setSnackbar', { status: 'success', text: `${this.layout.ad_name} Updated` })
        }
      } catch {
        this.$store.dispatch('setSnackbar', { status: 'error', text: 'Failed to Update Layout' })
      }
    },
    async deleteLayout () {
      try {
        await this.$Layouts.deleteLayout(this.layout.id)
        this.$store.dispatch('setSnackbar', { status: 'success', text: `${this.layout.ad_name} Deleted` })
        this.$emit('refresh')
        this.$emit('closeLayoutBuilder')
      } catch {
        this.$store.dispatch('setSnackbar', { status: 'error', text: 'Failed to Delete Layout' })
      }
    },
  }
}
</script>
<style scoped>
  .active {
    font-weight: bold;
    background: #e0e0e0;
    color: #000 !important;
  }
</style>